import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { Label } from 'reactstrap';
import Swal from 'sweetalert2';
import axios from '../../../api/axios';
import '../../parking.scss';

const layoutschema = Yup.object().shape({
    layouts: Yup.array().of(
        Yup.object().shape({
            parking_type: Yup.string().required('Parking Type is required'),
            floor_no: Yup.number()
                .integer('Must be a whole number')
                .required('Floor Number is required'),
            slot_start: Yup.number()
                .integer('Must be a whole number')
                .required('Slot Start is required'),
            slot_end: Yup.number()
                .integer('Must be a whole number')
                .required('Slot End is required'),
        })
    ),
});

const defaultValues = {
    layouts: [
        {
            parking_type: '',
            floor_no: '',
            start_slot_alphabet: '',
            slot_start: '',
            end_slot_alphabet: '',
            slot_end: '',
        },
    ],
};

const ParkingLayoutModal = ({ isOpen, toggle, property, onSubmit }) => {
    const [initialValues, setInitialValues] = useState(defaultValues);

    useEffect(() => {
        if (property?._id) {
            axios
                .get(`/parking_new/parking-layout/find-by-property-id`, {
                    params: { id: property._id, building_name: property?.building_name },
                })
                .then((response) => {
                    const { data } = response.data;
                    setInitialValues(data?.layouts ? { layouts: data.layouts } : defaultValues);
                })
                .catch(() => setInitialValues(defaultValues));
        }
    }, [property]);

    const handleSubmit = async (values, { setSubmitting }) => {
        const floorNumbers = values.layouts.map((l) => l.floor_no);
        if (new Set(floorNumbers).size !== floorNumbers.length) {
            Swal.fire({ title: 'Error!', text: 'Floor numbers must be unique.', icon: 'error', confirmButtonText: 'OK' });
            setSubmitting(false);
            return;
        }
        await onSubmit(values, property?._id, property?.building_name);
        setSubmitting(false);
        toggle();
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" className="parking-modal">
            <ModalHeader toggle={toggle}>Setup Parking Layout — {property?.title}</ModalHeader>
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={layoutschema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                    <Form>
                        <ModalBody>
                            <ParkingLayoutForm values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} />
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" onClick={toggle}>Cancel</Button>
                            <Button color="primary" type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Saving…' : 'Save Layout'}
                            </Button>
                        </ModalFooter>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

const ParkingLayoutForm = ({ values, errors, touched }) => (
    <FieldArray name="layouts">
        {({ push, remove }) => (
            <div>
                {values.layouts.map((_layout, index) => (
                    <div key={index} className="layout-row">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#8492a6', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Layout {index + 1}
                            </span>
                            <button
                                type="button"
                                className="btn-remove-layout"
                                onClick={() => remove(index)}
                            >
                                Remove
                            </button>
                        </div>
                        <div className="row">
                            {renderField(`layouts.${index}.parking_type`, 'Parking Type', 'select', errors, touched, [
                                { value: '', label: 'Select Type' },
                                { value: 'Underground Parking', label: 'Underground Parking' },
                                { value: 'Surface Stall Parking', label: 'Surface Stall Parking' },
                                { value: 'Visitor Parking', label: 'Visitor Parking' },
                            ])}
                            {renderField(`layouts.${index}.floor_no`, 'Floor Number', 'number', errors, touched)}
                            {renderField(`layouts.${index}.start_slot_alphabet`, 'Start Alphabet (Optional)', 'text', errors, touched)}
                            {renderField(`layouts.${index}.slot_start`, 'Slot Start', 'number', errors, touched)}
                            {renderField(`layouts.${index}.end_slot_alphabet`, 'End Alphabet (Optional)', 'text', errors, touched)}
                            {renderField(`layouts.${index}.slot_end`, 'Slot End', 'number', errors, touched)}
                        </div>
                    </div>
                ))}
                <button
                    type="button"
                    className="btn-add-layout"
                    onClick={() => push({ parking_type: '', floor_no: '', start_slot_alphabet: '', slot_start: '', slot_end: '', end_slot_alphabet: '' })}
                >
                    + Add Parking Layout
                </button>
            </div>
        )}
    </FieldArray>
);

const renderField = (name, label, type, errors, touched, options = []) => {
    const error = name.split('.').reduce((obj, key) => obj?.[key], errors);
    const isTouched = name.split('.').reduce((obj, key) => obj?.[key], touched);

    return (
        <div className="col-md-6 mb-3">
            <Label htmlFor={name} className="form-label">{label}</Label>
            {type === 'select' ? (
                <Field
                    as="select"
                    name={name}
                    className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                >
                    {options.map((option, idx) => (
                        <option key={idx} value={option.value}>{option.label}</option>
                    ))}
                </Field>
            ) : (
                <Field
                    type={type}
                    name={name}
                    className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                />
            )}
            {error && isTouched && (
                <div className="invalid-feedback d-block">{error}</div>
            )}
        </div>
    );
};

export { ParkingLayoutModal };